import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Typography, Tag, Tooltip, Empty } from 'antd';
import {
  FileOutlined,
  DownloadOutlined,
  UploadOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import PDFView from '../../components/pdf/PDFView';

const { Text } = Typography;

const FILE_KEY_OPTIONS = [
  { value: 'ky5', label: 'KY5' },
  { value: 'storeregistered', label: 'ทะเบียนร้านค้า' },
  { value: 'idcard', label: 'บัตรประชาชน' },
  { value: 'bookbank', label: 'สมุดบัญชี' },
];

const getFileIcon = (filename = '') => {
  if (filename.toLowerCase().endsWith('.pdf'))
    return <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />;
  return <FileImageOutlined style={{ color: '#1677ff', fontSize: 20 }} />;
};

/**
 * Modal for viewing and managing shop document files
 * Unified upload: one button per doc type handles both reupload and new upload
 */
function ShopFileModal({
  open,
  useritem,
  files,
  fileselected,
  app_hostname,
  onCancel,
  onSelectFile,
  onDownload,
  onUpload,
}) {
  const uploadRefs = useRef({});

  // Map key → existing file object (e.g. 'bookbank' → { filename: 'SHP-123_bookbank.png' })
  const fileMap = files.reduce((acc, f) => {
    const nameWithoutExt = f.filename.split('.')[0];
    const parts = nameWithoutExt.split('_');
    const key = parts.length > 1 ? parts[parts.length - 1] : nameWithoutExt;
    acc[key] = f;
    return acc;
  }, {});

  const renderFileView = () => {
    if (!fileselected) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<Text type='secondary'>เลือกไฟล์เพื่อดูตัวอย่าง</Text>}
        />
      );
    }

    const lowerFilename = fileselected.toLowerCase();
    const fileUrl = `${app_hostname}/api/account/shop/file/shopdocument/${useritem.shop_id}/${fileselected}`;

    if (lowerFilename.endsWith('.pdf')) return <PDFView filepath={fileUrl} />;
    if (
      lowerFilename.endsWith('.png') ||
      lowerFilename.endsWith('.jpg') ||
      lowerFilename.endsWith('.jpeg')
    ) {
      return (
        <img
          src={fileUrl}
          alt='document preview'
          style={{ width: '100%', borderRadius: 8, objectFit: 'contain' }}
        />
      );
    }
    return null;
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <FileOutlined style={{ fontSize: 18, color: '#1677ff' }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 16 }}>รหัสร้านค้า: {useritem.shop_id}</div>
            <Text type='secondary' style={{ fontSize: 13, fontWeight: 400 }}>
              {useritem.shop_name} &bull; {useritem.shop_province}
            </Text>
          </div>
        </div>
      }
      open={open}
      onCancel={onCancel}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button icon={<DownloadOutlined />} onClick={() => onDownload(useritem.shop_id)}>
            ดาวน์โหลดไฟล์ทั้งหมด (.zip)
          </Button>
          <Button onClick={onCancel}>ปิด</Button>
        </div>
      }
      width={1100}
      centered
      className='custom-modal'
      styles={{ body: { padding: '16px 24px' } }}
    >
      <div className='shop-file-modal-layout' style={{ display: 'flex', gap: 16, minHeight: 480 }}>
        <div
          className='shop-file-modal-sidebar'
          style={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}
        >
          <Text
            type='secondary'
            style={{
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: 1,
              marginBottom: '8px',
              display: 'block',
            }}
          >
            เอกสารทั้งหมด
          </Text>

          <div className='shop-file-grid'>
            {FILE_KEY_OPTIONS.map((option) => {
              const existingFile = fileMap[option.value];
              const cleanSelectedFilename = fileselected.includes('_')
                ? fileselected.substring(fileselected.indexOf('_') + 1)
                : fileselected;
              const isSelected = existingFile && cleanSelectedFilename === existingFile.filename;

              return (
                <div
                  key={option.value}
                  style={{
                    border: `1.5px solid ${isSelected ? '#1677ff' : existingFile ? '#e8e8e8' : '#faad14'}`,
                    borderRadius: 10,
                    padding: '10px 12px',
                    backgroundColor: isSelected ? '#e6f4ff' : existingFile ? '#fafafa' : '#fffbe6',
                    cursor: existingFile ? 'pointer' : 'default',
                    transition: 'all 0.2s',
                  }}
                  onClick={() => existingFile && onSelectFile(existingFile.filename)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    {existingFile ? (
                      getFileIcon(existingFile.filename)
                    ) : (
                      <FileOutlined style={{ color: '#faad14', fontSize: 20 }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        ellipsis
                        style={{
                          fontSize: 13,
                          display: 'block',
                          color: isSelected ? '#1677ff' : '#333',
                        }}
                      >
                        {option.label}
                      </Text>
                      {existingFile ? (
                        <Text type='secondary' style={{ fontSize: 11 }} ellipsis>
                          {existingFile.filename}
                        </Text>
                      ) : (
                        <Tag color='warning' style={{ fontSize: 10, marginTop: 2 }}>
                          ยังไม่มีไฟล์
                        </Tag>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 6 }}>
                    {existingFile && (
                      <Tooltip title='ดูตัวอย่าง'>
                        <Button
                          size='small'
                          icon={<EyeOutlined />}
                          type={isSelected ? 'primary' : 'default'}
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectFile(existingFile.filename);
                          }}
                        />
                      </Tooltip>
                    )}

                    <Tooltip title={existingFile ? 'อัปโหลดแทนไฟล์เดิม' : 'อัปโหลดไฟล์'}>
                      <Button
                        size='small'
                        icon={<UploadOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          uploadRefs.current[option.value]?.click();
                        }}
                      >
                        อัปโหลด
                      </Button>
                    </Tooltip>

                    <input
                      type='file'
                      ref={(el) => (uploadRefs.current[option.value] = el)}
                      style={{ display: 'none' }}
                      accept='image/jpeg,image/jpg,image/png,application/pdf'
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        onUpload(file, option.value);
                        e.target.value = '';
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div
          className='shop-file-modal-preview'
          style={{
            flex: 1,
            border: '1.5px solid #e8e8e8',
            borderRadius: 10,
            padding: 16,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#fafafa',
            overflow: 'hidden',
          }}
        >
          {renderFileView()}
        </div>
      </div>
    </Modal>
  );
}

ShopFileModal.propTypes = {
  open: PropTypes.bool.isRequired,
  useritem: PropTypes.object.isRequired,
  files: PropTypes.array.isRequired,
  fileselected: PropTypes.string,
  app_hostname: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSelectFile: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
};

ShopFileModal.defaultProps = {
  fileselected: '',
};

export default ShopFileModal;
